import React, { useState, useEffect, useCallback } from "react";
import { Search, Eye, RefreshCw, User, Mail, Phone, Calendar, LogIn } from "lucide-react";
import Pagination from "../components/PaginationComp";
import UserDetailsModal from "./UserDetailsModal";
import api from "../utils/api";
import { useAuth } from "../components/authentication/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const UsersTable = ({ onUserSelect }) => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalUsers: 0,
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [error, setError] = useState("");
  const { startImpersonating } = useAuth();
  const navigate = useNavigate();

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch users when page or search term changes
  useEffect(() => {
    fetchUsers();
  }, [currentPage, debouncedSearchTerm]);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const res = await api.get(`get-all-users`, {
        params: {
          page: currentPage,
          limit: 10,
          search: debouncedSearchTerm || undefined,
        },
      });

      setUsers(res.data.data || []);
      setPagination({
        currentPage: res.data.pagination?.currentPage || 1,
        totalPages: res.data.pagination?.totalPages || 1,
        totalUsers: res.data.total || 0,
      });
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load users");
      setUsers([]);
      setPagination({
        currentPage: 1,
        totalPages: 1,
        totalUsers: 0,
      });
      
      // If unauthorized, redirect to login (handled by interceptor)
      if (err.response?.status === 401) {
        return;
      }
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearchTerm]);

  const formatDate = (date) => {
    if (!date) return "—";
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: "bg-green-100 text-green-800 border border-green-200",
      trial: "bg-blue-100 text-blue-800 border border-blue-200",
      expired: "bg-red-100 text-red-800 border border-red-200",
      cancelled: "bg-gray-100 text-gray-800 border border-gray-200",
      pending: "bg-amber-100 text-amber-800 border border-amber-200",
    };

    return (
      <span
        className={`px-3 py-1 rounded-full text-xs font-medium ${
          styles[status?.toLowerCase()] || "bg-gray-100 text-gray-800 border border-gray-200"
        }`}
      >
        {(status || "expired").toUpperCase()}
      </span>
    );
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setShowUserModal(true);
    if (onUserSelect) {
      onUserSelect(user._id);
    }
  };

  const handleRefresh = () => {
    setSearchTerm("");
    setCurrentPage(1);
    fetchUsers();
  };

  const handleImpersonate = async (userId) => {
    try {
      const res = await startImpersonating(userId);
      if (res.success) {
        toast.success("Switched to Impersonate Mode");
        navigate("/table");
      } else {
        toast.error(res.message || "Failed to impersonate");
      }
    } catch (err) {
      toast.error("An error occurred while switching accounts.");
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex flex-col md:flex-row gap-4 md:gap-0 items-center justify-between">
          
          <div className="text-center md:text-left">
            <h2 className="text-2xl font-bold text-gray-800">Users Management</h2>
            <p className="text-gray-600 text-sm mt-1">
              Total: {pagination.totalUsers} users
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 items-center w-full md:w-auto">
            
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, email, or phone..."
                className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              />
            </div>

            <button
              onClick={handleRefresh}
              className="px-4 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg flex items-center gap-2 hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-md hover:shadow-lg"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="m-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl flex items-center justify-between">
          <span>{error}</span>
          <button
            onClick={handleRefresh}
            className="text-sm underline hover:text-red-800"
          >
            Try Again
          </button>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                User Details
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Subscription Status
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Expiry Date
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Joined Date
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              [...Array(5)].map((_, i) => (
                <tr key={i} className="animate-pulse">
                  <td className="px-6 py-4">
                    <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-48"></div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="h-6 bg-gray-200 rounded w-20"></div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="h-9 bg-gray-200 rounded w-20"></div>
                  </td>
                </tr>
              ))
            ) : users.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-12 text-center">
                  <div className="flex flex-col items-center justify-center text-gray-500">
                    <User className="h-12 w-12 mb-3 text-gray-400" />
                    <p className="text-lg font-medium">No users found</p>
                    <p className="text-sm mt-1">
                      {debouncedSearchTerm 
                        ? `No results for "${debouncedSearchTerm}"` 
                        : "No users in the system yet"}
                    </p>
                    {debouncedSearchTerm && (
                      <button
                        onClick={handleRefresh}
                        className="mt-3 text-blue-600 hover:text-blue-800 text-sm"
                      >
                        Clear search
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user._id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 bg-gradient-to-r from-blue-100 to-teal-100 rounded-full flex items-center justify-center">
                          <span className="font-semibold text-blue-600">
                            {user.owner?.charAt(0)?.toUpperCase() || "U"}
                          </span>
                        </div>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800">{user.owner || "No Name"}</p>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 text-sm text-gray-600 mt-1">
                          {user.email && (
                            <span className="flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {user.email}
                            </span>
                          )}
                          {user.mobile && (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {user.mobile}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>

                  <td className="px-6 py-4">
                    {getStatusBadge(user.subscription?.status)}
                    {user.subscription?.plan && (
                      <p className="text-xs text-gray-500 mt-1">
                        {user.subscription.plan}
                      </p>
                    )}
                  </td>

                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <span className="font-medium">
                        {formatDate(user.subscription?.expires_on)}
                      </span>
                    </div>
                  </td>

                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <span className="font-medium">
                        {formatDate(user.createdAt)}
                      </span>
                    </div>
                  </td>

                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleViewUser(user)}
                        title="View Details"
                        className="px-3 py-2 bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700 rounded-lg flex items-center justify-center hover:from-blue-100 hover:to-blue-200 transition-all duration-200 border border-blue-200 hover:border-blue-300"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleImpersonate(user._id)}
                        title="Impersonate"
                        className="px-3 py-2 bg-gradient-to-r from-orange-50 to-orange-100 text-orange-700 rounded-lg flex items-center justify-center hover:from-orange-100 hover:to-orange-200 transition-all duration-200 border border-orange-200 hover:border-orange-300"
                      >
                        <LogIn className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pagination.totalPages > 1 && (
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-600">
              Showing {users.length} of {pagination.totalUsers} users
            </p>
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>
      )}

      {showUserModal && selectedUser && (
        <UserDetailsModal
          user={selectedUser}
          onClose={() => {
            setShowUserModal(false);
            setSelectedUser(null);
          }}
          onRefresh={fetchUsers}
        />
      )}
    </div>
  );
};

export default UsersTable;